import React from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  breakpoints,
  headingStyle,
  bodyTextStyle,
  containerStyle,
  icones,
} from './designSystem';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

/* Peu d'espace sous les chiffres : les filtres suivent juste après, et leur
   barre porte déjà un filet (retour de Clément du 22/09). */
const HeroSection = styled.section`
  margin: 0;
  padding: 48px 0 8px;

  @media (min-width: ${breakpoints.mobile}) {
    padding: 64px 0 8px;
  }
`;

const Container = styled.div`
  ${containerStyle}
`;

const Title = styled.h1`
  ${headingStyle}
  font-size: 1.75rem;
  margin-bottom: 16px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.75rem;
  }
`;

const Emphasis = styled.span`
  color: ${colors.bleu};
`;

const IntroText = styled.p`
  ${bodyTextStyle}
  font-size: 1.1rem;
  line-height: 1.75;
  max-width: 720px;
  margin-bottom: 32px;
  color: ${colors.textSecondary};
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 24px 0 16px;
  border-top: 1px solid ${colors.borderLight};
  justify-items: center;

  @media (min-width: ${breakpoints.mobile}) {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    justify-items: unset;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
`;

const StatIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${(p) => p.$bg || colors.bleuPastel};
`;

const StatIconImg = styled.img`
  width: 22px;
  height: auto;
  object-fit: contain;
`;

const StatValue = styled.div`
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 1.1rem;
  color: ${colors.textDark};
`;

const StatLabel = styled.div`
  font-family: ${fonts.body};
  font-size: 0.8rem;
  color: ${colors.textSecondary};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EnLigne = styled.p`
  ${bodyTextStyle}
  font-size: 1.05rem;
  margin: -16px 0 32px;
  color: ${colors.textDark};
`;

/** `nbEnLigne` : les ateliers réservables en ligne de la ville (lot 3). */
export default function VilleHero({
  name,
  villeStats,
  seoVille,
  nbEnLigne = 0,
  nbBonus = 0,
}) {
  const stats = villeStats || {};
  const intro = (seoVille && seoVille.introduction_seo) || null;
  const h1 = (seoVille && seoVille.h1_ville) || null;
  // Les ateliers du Bonus sont un sous-ensemble des ateliers réservables en
  // ligne (les deux viennent de la fiche Fixer Lite) : la clause n'a de sens
  // qu'accrochée à la phrase des réservables.
  const clauseBonus =
    nbBonus > 0
      ? `, dont ${nbBonus} ${
          nbBonus > 1 ? 'appliquent' : 'applique'
        } le Bonus Répar`
      : '';

  return (
    <HeroSection>
      <Container>
        <Title>
          {h1 || (
            <>
              Ateliers v&eacute;lo &agrave; <Emphasis>{name || ''}</Emphasis>
            </>
          )}
        </Title>

        {intro && <IntroText>{intro}</IntroText>}
        {nbEnLigne > 0 && (
          <EnLigne>
            <b>
              {nbEnLigne} atelier{nbEnLigne > 1 ? 's' : ''}
            </b>{' '}
            à réserver en ligne à {name || 'cette ville'}
            {clauseBonus}.
          </EnLigne>
        )}

        <StatsBar>
          <StatItem>
            <StatIconBox $bg={colors.vertPastel}>
              <StatIconImg src={icones.shop} alt="" />
            </StatIconBox>
            <div>
              <StatValue>{stats.nb_shops || 0}</StatValue>
              <StatLabel>&eacute;tablissements</StatLabel>
            </div>
          </StatItem>
          <StatItem>
            <StatIconBox $bg={colors.bleuPastel}>
              <StatIconImg src={icones.pouceOk} alt="" />
            </StatIconBox>
            <div>
              <StatValue>
                {stats.note_moyenne ? `${stats.note_moyenne}/5` : '-'}
              </StatValue>
              <StatLabel>note moyenne</StatLabel>
            </div>
          </StatItem>
          <StatItem>
            <StatIconBox $bg={colors.violetPastel}>
              <StatIconImg src={icones.checklist} alt="" />
            </StatIconBox>
            <div>
              <StatValue>{stats.nb_avis_total || 0}</StatValue>
              <StatLabel>avis clients</StatLabel>
            </div>
          </StatItem>
          <StatItem>
            <StatIconBox $bg={colors.orangePastel}>
              <StatIconImg src={icones.reparationMain} alt="" />
            </StatIconBox>
            <div>
              <StatValue>{stats.nb_services_uniques || 0}</StatValue>
              <StatLabel>services diff&eacute;rents</StatLabel>
            </div>
          </StatItem>
        </StatsBar>
      </Container>
    </HeroSection>
  );
}
